// ============================================================================
// GRANULR — marker-based granular synth
// Ported from zya/granular (https://github.com/zya/granular) by ZYA (MIT).
// Adapted for the AZOPSOFT legacy-apps framework:
//   - Vanilla canvas waveform + grain display (no Processing.js)
//   - Marker-based interaction: left-click places a persistent marker that
//     acts as a continuous "finger" (a voice spawning grains). Left-click a
//     marker removes it. Multiple markers = polyphony.
//   - Shared FX rack (afxrack) + LFO system from amenpi/kaeps
//   - Bucket drag-drop + file drop for loading samples
//   - URL state save/load (pako-compressed)
// ============================================================================

const { qs, qsa, build, apc, alertmessage, prompter } = require('../../libs/qs')
const midi = require('../common/midi')

// Use localStorage theme if available, otherwise fall back to server-provided window.Theme
let currentTheme;
try {
  const storedTheme = localStorage.getItem('currentTheme');
  if (storedTheme) currentTheme = JSON.parse(storedTheme);
} catch (e) {}
if (!currentTheme) currentTheme = window.Theme;

const themeColors = Object.values(currentTheme.data)
const Sortable = require("sortablejs");
const themeRgb = themeColors.map((el) => {
    return { r: parseInt(el.slice(1, 3), 16), g: parseInt(el.slice(3, 5), 16), b: parseInt(el.slice(5, 7), 16) }
})

window.CONTROLZ = {}
window.themeColors = themeColors
window.currentTheme = currentTheme
window.themeRgb = themeRgb

const getThemeColors = () => window.themeColors || themeColors;

// LFO bank setup (matches amenpi/kaeps pattern)
window.openedbank = 'granulr'
window.lfo = window.lfo || {}
window.lfo['granulr'] = {}
window.lfoz = window.lfo['granulr']
window.lfoindex = 0
window.lfobtns = window.lfobtns || {}
window.lfobtns['granulr'] = []
window.lfolinks = window.lfolinks || {}
window.lfolinks['granulr'] = {}
window.bandlinks = {}
window.macrolinks = {}
window.midilinks = {}

const uuid = require('uuid').v4
const engine = require('../common/audioengine')
const fxs = require('../common/audiofx')
const { faderH, dualrange } = require('../common/ctrlz.js')
const Recorder = require('../common/Recorder')
const lfo = require('../common/lfo')
const transport = require('../common/transport')
const pako = require('pako')
const getamepicadres = require('../common/getamepicadres')

const store = {}

const arrayMove = (array, from, to) => {
    const startIndex = to < 0 ? array.length + to : to
    const item = array.splice(from, 1)[0]
    array.splice(startIndex, 0, item)
}

const debounce = (func, wait) => {
    let timeout;
    const fn = function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
    fn.cancel = () => { clearTimeout(timeout); };
    return fn;
}

// ---------------------------------------------------------------------------
// Grain — a single grain of sound (ported from zya/granular grain class)
// ---------------------------------------------------------------------------
function grain(ctx, buffer, positionx, positiony, w, h, attack, release, spread, pan, transpose, output, grainCanvasCtx) {
    const that = this;
    const now = ctx.currentTime;

    this.source = ctx.createBufferSource();
    this.source.playbackRate.value = this.source.playbackRate.value * transpose;
    this.source.buffer = buffer;

    this.gain = ctx.createGain();

    // Panner — only some grains are panned for performance (matches original)
    const yes = Math.floor(Math.random() * 3);
    if (yes === 1) {
        this.panner = ctx.createPanner();
        this.panner.panningModel = "equalpower";
        this.panner.distanceModel = "linear";
        this.panner.setPosition((Math.random() * pan * -1) + (Math.random() * pan), 0, 0);
        this.source.connect(this.panner);
        this.panner.connect(this.gain);
    } else {
        this.source.connect(this.gain);
    }

    this.gain.connect(output);

    // pixels → seconds offset
    this.positionx = positionx;
    this.offset = this.positionx * (buffer.duration / w);

    // pixels → amplitude (Y inverted: top = loud)
    this.positiony = positiony;
    this.amp = this.positiony / h;
    this.amp = map(this.amp, 0.0, 1.0, 1.0, 0.0) * 0.7;

    this.attack = attack * 0.4;
    this.release = release * 1.5;
    if (this.release < 0) this.release = 0.1;
    this.spread = spread;

    this.randomoffset = (Math.random() * this.spread) - (this.spread / 2);

    // envelope + start
    this.source.start(now, Math.max(0, this.offset + this.randomoffset), this.attack + this.release);
    this.gain.gain.setValueAtTime(0.0, now);
    this.gain.gain.linearRampToValueAtTime(this.amp, now + this.attack);
    this.gain.gain.linearRampToValueAtTime(0, now + (this.attack + this.release));

    // garbage collection
    this.source.stop(now + this.attack + this.release + 0.1);
    const tms = (this.attack + this.release) * 1000;
    setTimeout(() => {
        try { that.gain.disconnect(); } catch(e) {}
        if (yes === 1) { try { that.panner.disconnect(); } catch(e) {} }
    }, tms + 200);

    // visual: transient vertical line on grain canvas using theme colors
    this.randomoffsetinpixels = this.randomoffset / (buffer.duration / w);
    if (grainCanvasCtx) {
        const lx = this.positionx + this.randomoffsetinpixels;
        const colors = getThemeColors();
        // pick a random theme color from the brighter range for variety
        const idx = 9 + Math.floor(Math.random() * 7); // indices 9-15 (brighter colors)
        const c = colors[idx] || colors[9] || '#ffff00';
        grainCanvasCtx.strokeStyle = c;
        grainCanvasCtx.lineWidth = 1.5;
        grainCanvasCtx.beginPath();
        grainCanvasCtx.moveTo(lx, 0);
        grainCanvasCtx.lineTo(lx, h);
        grainCanvasCtx.stroke();
        // fade out after 200ms
        setTimeout(() => {
            grainCanvasCtx.clearRect(lx - 1, 0, 3, h);
        }, 200);
    }
}

function map(val, a, b, c, d) {
    return c + (d - c) * ((val - a) / (b - a));
}

// ---------------------------------------------------------------------------
// Voice — a marker-driven continuous grain spawner (ported from voice class)
// ---------------------------------------------------------------------------
function Voice(ctx, buffer, marker, w, h, paramsFn, output, grainCanvasCtx) {
    this.marker = marker;
    this.grains = [];
    this.graincount = 0;
    const that = this;

    this.play = function () {
        // Read live params on every grain — fader changes take effect immediately
        const p = paramsFn();
        const g = new grain(ctx, buffer, that.marker.x, that.marker.y, w, h,
            p.attack, p.release, p.spread, p.pan, p.transpose, output, grainCanvasCtx);
        that.grains[that.graincount] = g;
        that.graincount += 1;
        if (that.graincount > 30) that.graincount = 0;

        // Y position → grain density (top = dense, bottom = sparse)
        // Y modulates the base density from the fader: top boosts density,
        // bottom reduces it. Range: ~70ms (dense) to ~570ms (sparse) interval.
        const yNorm = 1.0 - (that.marker.y / h); // 1 at top, 0 at bottom
        const baseDens = map(p.density, 1, 0, 0, 1);
        const dens = Math.max(0, Math.min(1, baseDens * (0.3 + yNorm * 0.7)));
        that.interval = (dens * 500) + 70;
        that.timeout = setTimeout(that.play, that.interval);
    };
    this.play();

    this.stop = function () {
        clearTimeout(this.timeout);
    };
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------
async function init() {

    await midi();
    window.midilearn = false;
    window.midiLearnElement = null;

    let _isLoading = false;
    let debouncedSave = () => {
        if (_isLoading) return;
        if (typeof saveStateToURL === 'function') saveStateToURL();
    };
    let debouncedSaveStructural = () => {
        if (_isLoading) return;
        if (typeof saveStateToURL === 'function') saveStateToURL();
    };

    store.pitcharray = [];
    for (let i = 0; i <= 200; i++) store.pitcharray.push(0.01 + i * 0.05);
    store.fxrack = [];
    store.markers = [];      // {id, x, y, voice, element, lineElement}
    store.buffer = null;     // AudioBuffer
    store.bufferData = null; // Float32Array channel 0
    store.transpose = 1;
    store.isloaded = false;

    // ---- Audio context + FX rack ----
    store.audioContext = new AudioContext()
    window.audioContext = store.audioContext
    transport.init(store.audioContext, { bpm: 120 })
    transport._bindExisting(document)

    const arack = await require('../common/afxrack').init(store, null, store.audioContext)
    const out = store.audioContext.createGain()
    arack.output.connect(out)
    out.connect(store.audioContext.destination)

    // ---- faderH controls ----
    store.pitchknob = qs('#pitchknob')
    faderH(store.pitchknob)
    store.playergain = qs('#playergain')
    faderH(store.playergain)
    store.outvolume = qs('#outvolume')
    faderH(store.outvolume)
    store.attack = qs('#attack')
    faderH(store.attack)
    store.release = qs('#release')
    faderH(store.release)
    store.density = qs('#density')
    faderH(store.density)
    store.spread = qs('#spread')
    faderH(store.spread)
    store.pan = qs('#pan')
    faderH(store.pan)

    // ---- Canvas refs ----
    // #vizu = live output waveform + spectrogram (row 1, amenpi-style)
    // #grainvizu = granular synth sample waveform (row 2, markers placed here)
    // #graincanvas = overlay for grain visualization (row 2, on top of #grainvizu)
    const outputCanvas = qs('#vizu')
    const outputCtx = outputCanvas.getContext('2d')
    const grainvizuCanvas = qs('#grainvizu')
    const grainvizuCtx = grainvizuCanvas.getContext('2d')
    const grainCanvas = qs('#graincanvas')
    const grainCtx = grainCanvas.getContext('2d')
    const scanholder = qs('.granulr-scanholder')
    const helpEl = qs('#granulrhelp')

    // Cadre PNG overlay (same as amenpi — theme-colored border overlay on #vizu)
    let cadres = null
    window.addEventListener('themechange', async () => {
        setTimeout(async () => {
            cadres = await getamepicadres(store)
            const currentColors = getThemeColors();
            chromas = new chroma.ColorScale({
                colors: [currentColors[0] || '#000000', currentColors[4] || '#444444', currentColors[8] || '#888888', currentColors[11] || '#cccccc'],
                positions: [0, .25, .75, 1],
                mode: 'rgb',
                limits: [0, 300]
            });
        }, 100)
    })
    // Dispatch themechange immediately (init is deferred via setTimeout,
    // so window.load has already fired — can't rely on it)
    setTimeout(async () => {
        window.dispatchEvent(new Event('themechange'))
    }, 200)
    if (window.themeColors && window.themeColors.length > 0) {
        cadres = await getamepicadres(store)
    }

    // Live output analyser + spectrogram (matches amenpi pattern)
    const oscillo = store.audioContext.createAnalyser()
    oscillo.fftSize = 2048
    const oscilloBufferLen = oscillo.frequencyBinCount
    const oscilloData = new Uint8Array(oscilloBufferLen)
    const analyser = store.audioContext.createAnalyser()
    analyser.smoothingTimeConstant = 0.3
    analyser.fftSize = 512
    const specCanvas = build('canvas')
    const specCtx = specCanvas.getContext('2d')
    const tempCanvas = build('canvas')
    const tempCtx = tempCanvas.getContext('2d')
    specCanvas.width = tempCanvas.width = 200
    specCanvas.height = tempCanvas.height = 200

    // Spectrogram color scale using theme colors (matches amenpi)
    const initColors = getThemeColors();
    let chromas = new chroma.ColorScale({
        colors: [initColors[0] || '#000000', initColors[4] || '#444444', initColors[8] || '#888888', initColors[11] || '#cccccc'],
        positions: [0, .25, .75, 1],
        mode: 'rgb',
        limits: [0, 300]
    })

    // Connect output to analysers
    out.connect(oscillo)
    out.connect(analyser)

    let w = grainvizuCanvas.width
    let h = grainvizuCanvas.height

    // Resize canvases to match container
    const resizeCanvases = () => {
        const rect = scanholder.getBoundingClientRect()
        w = rect.width
        h = rect.height
        grainvizuCanvas.width = w
        grainvizuCanvas.height = h
        grainCanvas.width = w
        grainCanvas.height = h
        const orect = outputCanvas.getBoundingClientRect()
        outputCanvas.width = orect.width
        outputCanvas.height = orect.height
        if (store.isloaded) drawBuffer()
        // Reposition markers proportionally
        repositionMarkers()
    }

    const repositionMarkers = () => {
        store.markers.forEach(m => {
            if (m.element) {
                m.element.style.left = m.x + 'px'
                m.element.style.top = m.y + 'px'
            }
            if (m.lineElement) {
                m.lineElement.style.left = m.x + 'px'
                m.lineElement.style.height = h + 'px'
            }
        })
    }

    // ---- Waveform drawing (vanilla canvas, on #grainvizu) ----
    function drawBuffer() {
        if (!store.bufferData) return
        const step = Math.ceil(store.bufferData.length / w)
        const amp = h / 2
        grainvizuCtx.fillStyle = getThemeColors()[0] || '#000000'
        grainvizuCtx.fillRect(0, 0, w, h)
        grainvizuCtx.fillStyle = getThemeColors()[8] || '#888888'
        for (let i = 0; i < w; i++) {
            let min = 1.0, max = -1.0
            for (let j = 0; j < step; j++) {
                const datum = store.bufferData[(i * step) + j]
                if (datum < min) min = datum
                else if (datum > max) max = datum
            }
            grainvizuCtx.fillRect(i, (1 + min) * amp, 1, Math.max(1, (max - min) * amp))
        }
    }

    // ---- Load audio buffer ----
    const loadBuffer = (arrayBuffer) => {
        store.audioContext.decodeAudioData(arrayBuffer, (b) => {
            store.buffer = b
            store.bufferData = b.getChannelData(0)
            store.isloaded = true
            drawBuffer()
            if (helpEl) helpEl.style.display = 'none'
            // Restart any existing markers' voices with new buffer
            restartAllVoices()
            debouncedSave()
        }, () => {
            console.error('[granulr] decodeAudioData failed')
            if (alertmessage) alertmessage('failed to decode audio')
        })
    }

    // ---- Marker management ----
    const getParams = () => ({
        attack: Number(store.attack.value),
        release: Number(store.release.value),
        density: Number(store.density.value),
        spread: Number(store.spread.value),
        pan: Number(store.pan.value),
        transpose: store.transpose * store.pitcharray[parseInt(store.pitchknob.value) + 100]
    })

    const createMarker = (x, y) => {
        if (!store.isloaded) return
        // Clamp inside canvas
        x = Math.max(0, Math.min(w, x))
        y = Math.max(0, Math.min(h, y))

        const id = uuid()
        const markerEl = build('div')
        markerEl.classList.add('granulr-marker')
        markerEl.style.left = x + 'px'
        markerEl.style.top = y + 'px'
        markerEl.dataset.id = id
        scanholder.appendChild(markerEl)

        const lineEl = build('div')
        lineEl.classList.add('granulr-marker-line')
        lineEl.style.left = x + 'px'
        lineEl.style.height = h + 'px'
        scanholder.appendChild(lineEl)

        const marker = { id, x, y, element: markerEl, lineElement: lineEl, voice: null }
        marker.voice = new Voice(store.audioContext, store.buffer, marker, w, h,
            getParams, arack.input, grainCtx)
        store.markers.push(marker)

        // Right-click on marker → remove it (prevent default context menu)
        markerEl.addEventListener('contextmenu', (e) => {
            e.preventDefault()
            e.stopPropagation()
            removeMarker(marker)
            debouncedSaveStructural()
        })

        // Left-click drag → reposition marker (audio updates in real-time,
        // since Voice reads marker.x/marker.y each time it spawns a grain)
        let isDragging = false
        markerEl.addEventListener('mousedown', (e) => {
            if (e.button !== 0) return // only left button
            e.preventDefault()
            e.stopPropagation()
            isDragging = true
            markerEl.style.cursor = 'grabbing'
            // Voice keeps running — grains pick up new marker.x/y in real-time
        })

        const onMove = (e) => {
            if (!isDragging) return
            const rect = scanholder.getBoundingClientRect()
            let nx = e.clientX - rect.left
            let ny = e.clientY - rect.top
            nx = Math.max(0, Math.min(w, nx))
            ny = Math.max(0, Math.min(h, ny))
            marker.x = nx
            marker.y = ny
            markerEl.style.left = nx + 'px'
            markerEl.style.top = ny + 'px'
            lineEl.style.left = nx + 'px'
        }

        const onUp = (e) => {
            if (!isDragging) return
            isDragging = false
            markerEl.style.cursor = 'pointer'
            debouncedSaveStructural()
        }

        document.addEventListener('mousemove', onMove)
        document.addEventListener('mouseup', onUp)
        // Clean up listeners when marker is removed
        marker._cleanup = () => {
            document.removeEventListener('mousemove', onMove)
            document.removeEventListener('mouseup', onUp)
        }

        debouncedSaveStructural()
        return marker
    }

    const removeMarker = (marker) => {
        if (marker.voice) marker.voice.stop()
        if (marker._cleanup) marker._cleanup()
        if (marker.element) marker.element.remove()
        if (marker.lineElement) marker.lineElement.remove()
        const idx = store.markers.indexOf(marker)
        if (idx >= 0) store.markers.splice(idx, 1)
    }

    const clearAllMarkers = () => {
        store.markers.slice().forEach(removeMarker)
        debouncedSaveStructural()
    }

    const restartAllVoices = () => {
        // Stop old voices and start new ones with current buffer
        store.markers.forEach(m => {
            if (m.voice) m.voice.stop()
            m.voice = new Voice(store.audioContext, store.buffer, m, w, h,
                getParams, arack.input, grainCtx)
        })
    }

    // ---- Canvas left-click → place marker (right-click on marker removes it) ----
    scanholder.addEventListener('click', (e) => {
        if (!store.isloaded) return
        if (e.button !== 0) return // only left-click places markers
        // Don't place if clicking on a marker (markers handle their own events)
        if (e.target.classList.contains('granulr-marker') ||
            e.target.classList.contains('granulr-marker-line')) return
        const rect = scanholder.getBoundingClientRect()
        const x = e.clientX - rect.left
        const y = e.clientY - rect.top
        createMarker(x, y)
    })

    // Prevent default context menu on the scanholder (so right-click works for marker removal)
    scanholder.addEventListener('contextmenu', (e) => e.preventDefault())

    // ---- Clear markers button ----
    qs('#clearmarkers').addEventListener('click', clearAllMarkers)

    // ---- Transpose buttons ----
    qs('#transposeminus').addEventListener('click', () => {
        store.transpose = store.transpose * 0.5
        debouncedSave()
    })
    qs('#transposeplus').addEventListener('click', () => {
        store.transpose = store.transpose * 2
        debouncedSave()
    })

    // ---- Tempo controls ----
    const tempoEl = qs('#tempo')
    qs('#tempoup').addEventListener('click', () => {
        tempoEl.value = Number(tempoEl.value) + 1
        document.body.dispatchEvent(new CustomEvent('bpmchange', { detail: { bpm: Number(tempoEl.value) } }))
        debouncedSave()
    })
    qs('#tempodown').addEventListener('click', () => {
        tempoEl.value = Math.max(10, Number(tempoEl.value) - 1)
        document.body.dispatchEvent(new CustomEvent('bpmchange', { detail: { bpm: Number(tempoEl.value) } }))
        debouncedSave()
    })
    qs('#taptempo').addEventListener('click', () => {
        document.body.dispatchEvent(new CustomEvent('taptempo'))
        debouncedSave()
    })
    tempoEl.addEventListener('input', () => {
        document.body.dispatchEvent(new CustomEvent('bpmchange', { detail: { bpm: Number(tempoEl.value) } }))
        debouncedSave()
    })

    // ---- FX list show/hide (matches kaeps/amenpi pattern) ----
    // Note: #fxlist is populated by afxrack.js — don't duplicate here
    document.addEventListener('click', (e) => {
        const fxlistbox = qs('#fxlist')
        const fxtitle = qs('#fxracktitle')
        if (!fxlistbox || !fxtitle) return
        let target = e.target
        let found = false
        if (target == fxlistbox || target == fxtitle) {
            found = true
        } else {
            while (target.parentNode) {
                if (target == fxlistbox || target == fxtitle) { found = true; break }
                target = target.parentNode
            }
        }
        if (!found) fxlistbox.classList.add('hidden')
    })
    qs('#fxracktitle').addEventListener('click', (e) => {
        e.preventDefault()
        qs('#fxlist').classList.remove('hidden')
    })

    // ---- LFO add button ----
    qs('#addlfo').addEventListener('click', () => {
        lfo.createlfo()
        debouncedSaveStructural()
    })
    document.body.addEventListener('lfolinked', debouncedSaveStructural)
    document.body.addEventListener('unlinkfader', debouncedSaveStructural)

    // ---- Drag-drop onto waveform (bucket items + native files) ----
    scanholder.addEventListener('dragover', (e) => {
        e.preventDefault()
        if (e.dataTransfer.types.includes('application/x-bucket-item-id') ||
            e.dataTransfer.types.includes('Files')) {
            e.dataTransfer.dropEffect = 'copy'
            scanholder.style.outline = '2px dashed var(--cf)'
        }
    })
    scanholder.addEventListener('dragleave', () => {
        scanholder.style.outline = ''
    })
    scanholder.addEventListener('drop', async (e) => {
        e.preventDefault()
        scanholder.style.outline = ''

        // Bucket item drop
        const itemId = e.dataTransfer.getData('application/x-bucket-item-id')
        if (itemId && window._granulrBucket && window._granulrBucket.bucket) {
            try {
                const dbItems = await window._granulrBucket.dbGetAll(window._granulrBucket.bucket.bucketId)
                const item = dbItems.find(i => i.id === itemId)
                if (item && item.wav) {
                    loadBuffer(item.wav.slice(0))
                }
            } catch (err) {
                console.error('[granulr] bucket drop failed', err)
            }
            return
        }

        // Native file drop
        const file = e.dataTransfer.files[0]
        if (file) {
            const reader = new FileReader()
            reader.onload = (ev) => loadBuffer(ev.target.result)
            reader.readAsArrayBuffer(file)
        }
    })

    // ---- Bucket integration ----
    const initLegacyBucket = require('./legacy-bucket-integration.js')
    let _granulrBucket = null
    try {
        _granulrBucket = initLegacyBucket({
            appName: 'granulr',
            bucketContentSelector: '.bucketcontent',
            bucketPanelSelector: '.xtrc-bucket-panel',
            onItemDrop: null, // handled by scanholder drop above
        })
    } catch (e) {
        console.error('[granulr] bucket init failed', e)
    }
    window._granulrBucket = _granulrBucket

    // ---- Record button → Recorder → hidden download link → bucket ----
    // (matches kaeps/amenpi pattern: Recorder exports WAV as a hidden <a>
    // download link with GRANULR_REC_ prefix; a MutationObserver intercepts
    // the link and adds the WAV to the bucket instead of auto-downloading)
    const recorder = Recorder(out)
    let isrecording = false
    let rec_i = 0

    function startRecording() {
        recorder.record()
    }

    function stopRecording() {
        recorder.stop()
        createDownloadLink()
        recorder.clear()
    }

    function createDownloadLink() {
        recorder.exportWAV(function (blob) {
            const url = URL.createObjectURL(blob)
            const hf = build('a')
            hf.classList.add('hidden')
            hf.href = url
            hf.download = 'GRANULR_REC_' + rec_i + '.wav'
            rec_i++
            qs('body').appendChild(hf)
            hf.click()
        })
    }

    qs('#record').addEventListener('click', (e) => {
        isrecording = !isrecording
        e.target.classList.toggle('active')
        if (isrecording) {
            startRecording()
        } else {
            stopRecording()
        }
    })

    // Patch record button: when recording stops, watch for the GRANULR_REC_
    // download link and add the WAV to the bucket (instead of auto-download,
    // which is blocked by legacy-bucket-integration.js prefix interception)
    const recBtn = qs('#record')
    if (recBtn && _granulrBucket && _granulrBucket.bucket) {
        recBtn.addEventListener('click', () => {
            // Capture phase fires BEFORE the original handler toggles 'active'.
            // So 'active' is present when we're about to STOP recording.
            if (recBtn.classList.contains('active')) {
                const observer = new MutationObserver((mutations) => {
                    for (const mut of mutations) {
                        for (const node of mut.addedNodes) {
                            if (node.tagName === 'A' && node.download && node.download.startsWith('GRANULR_REC')) {
                                observer.disconnect()
                                fetch(node.href)
                                    .then(r => r.blob())
                                    .then(blob => blob.arrayBuffer())
                                    .then(data => {
                                        _granulrBucket.bucket.add({
                                            source: 'granulr',
                                            name: node.download,
                                            wavLike: data,
                                            fileType: 'wav'
                                        }).catch(() => {})
                                    })
                                    .catch(() => {})
                                node.remove()
                            }
                        }
                    }
                })
                observer.observe(document.body, { childList: true, subtree: false })
                setTimeout(() => observer.disconnect(), 5000)
            }
        }, true)
    }

    // ---- Draw loop: LFO tick + grain visualization + gain updates ----
    const draw = () => {
        window.requestAnimationFrame(draw)

        // LFO tick (matches kaeps pattern)
        const _lfolinks = window.lfolinks[window.openedbank] || {}
        for (let i in _lfolinks) {
            const x = Number(_lfolinks[i].input.max)
            const u = Number(_lfolinks[i].input.min)
            const lfoval = (_lfolinks[i].lfo.value() + 50)
            const y = Number(_lfolinks[i].lfo.seed.value)
            const z = ((x / 50) * (y * 1))
            let fixed = 0
            const step = "" + _lfolinks[i].input.step
            if (step.includes('.')) fixed = step.split('.')[1].length
            let val = 0
            if (u < 0) {
                if (lfoval > 50) {
                    val = (((x / 50) * (lfoval - 50)) + z)
                } else {
                    val = (-(Math.abs(u) - (((x / 50) * lfoval) + z)))
                }
            } else {
                val = ((x / 100) * lfoval) + (((x / 100) * y))
            }
            if (!fixed) val = parseInt(val)
            else val = Number(val.toFixed(Number(fixed)))
            _lfolinks[i].input.value = val
            _lfolinks[i].input.dispatchEvent(new Event('change'))
        }

        // Update gains from faders
        arack.input.gain.value = Number(store.playergain.value)
        out.gain.value = Number(store.outvolume.value)

        // ---- Live output waveform + spectrogram (amenpi-style, on #vizu) ----
        const colors = getThemeColors()
        const ow = outputCanvas.width
        const oh = outputCanvas.height
        outputCtx.fillStyle = colors[0] || '#000'
        outputCtx.fillRect(0, 0, ow, oh)

        // Oscilloscope (left 2/3)
        oscillo.getByteTimeDomainData(oscilloData)
        outputCtx.lineWidth = 1
        outputCtx.strokeStyle = colors[9] || '#ffff00'
        outputCtx.beginPath()
        const sliceWidth = (ow * 0.66) * 1.0 / oscilloBufferLen
        let ox = 0
        for (let i = 0; i < oscilloBufferLen; i++) {
            const v = oscilloData[i] / 128.0
            const y = (v * oh) / 2
            if (i === 0) outputCtx.moveTo(ox, y)
            else outputCtx.lineTo(ox, y)
            ox += sliceWidth
        }
        outputCtx.lineTo(ow * 0.66, oh / 2)
        outputCtx.stroke()

        // Spectrogram (right 1/3) — scroll left, using theme colors via chroma
        const spearray = new Uint8Array(analyser.frequencyBinCount)
        analyser.getByteFrequencyData(spearray)
        tempCtx.drawImage(specCanvas, 0, 0, 200, 200)
        for (let i = 0; i < spearray.length; i++) {
            const value = spearray[i]
            const color = chromas.getColor(value).hex()
            specCtx.fillStyle = color
            specCtx.fillRect(200 - 1, 200 - i, 1, 1)
        }
        specCtx.translate(-1, 0)
        specCtx.drawImage(tempCanvas, 0, 0, 200, 200)
        specCtx.setTransform(1, 0, 0, 1, 0, 0)
        outputCtx.drawImage(specCanvas, ow * 0.66, 0, ow * 0.34, oh)

        // Cadre PNG overlay (same as amenpi — theme-colored border frame)
        if (cadres && cadres.cadreimg && cadres.cadreimg[0]) {
            outputCtx.drawImage(cadres.cadreimg[0], 0, 0, ow, oh)
        }

        // Grain canvas: grains draw their own colorful lines and self-clear after 200ms.
        // Periodically clear any leftover pixels from stopped voices.
        if (Date.now() % 500 < 16) grainCtx.clearRect(0, 0, w, h)
    }

    // ---- Resize handling ----
    window.addEventListener('resize', resizeCanvases)
    resizeCanvases()

    // ============================================================================
    // URL STATE SAVE / LOAD (pako-compressed, matches kaeps pattern)
    // ============================================================================
    // Short keys: gn=gain, pt=pitch, vo=volume, bp=bpm, tr=transpose,
    //   at=attack, re=release, de=density, sp=spread, pn=pan,
    //   mk=markers, f=FX, l=LFO, k=links, cv=curves

    const urlSafeBase64Encode = (bytes) => {
        let binary = '';
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    };

    const urlSafeBase64Decode = (str) => {
        const padded = str.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - str.length % 4) % 4);
        const binary = atob(padded);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        return bytes;
    };

    const serializeState = () => {
        const bank = window.openedbank
        const lfoBank = window.lfo[bank] || {}
        const linkBank = window.lfolinks[bank] || {}

        const lfoIds = Object.keys(lfoBank)
        const lfoIdToIdx = {}
        lfoIds.forEach((id, i) => { lfoIdToIdx[id] = i })

        // FX: [type, isOn, {controlName: value}]
        const fxArr = (store.fxrack || []).map(fx => {
            const fxEl = qs(`.fxhold[data-id="${fx.id}"]`)
            const controls = {}
            if (fxEl) {
                fxEl.querySelectorAll('.ctrlinput').forEach(input => {
                    if (input.dataset.label) controls[input.dataset.label] = input.value
                })
            }
            return [fx.name, fx.ison ? 1 : 0, controls]
        })

        // LFO: [name, wave, freq, amp, seed, syncToBPM, quantizeValue, phaseOffset]
        const lfoArr = lfoIds.map(id => {
            const lfoInst = lfoBank[id]
            if (!lfoInst || !lfoInst.get) return null
            const d = lfoInst.get()
            return [d.name || '', d.wave || 'sin', d.freq || '0.5', d.amp || '50',
                    d.seed || '0', d.syncToBPM ? 1 : 0, d.quantizeValue || '1/4', d.phaseOffset || '0']
        }).filter(Boolean)

        // Links: [lfoIdx, fxIdx(-1=global), controlName]
        const linkArr = []
        for (let linkid in linkBank) {
            const link = linkBank[linkid]
            if (!link) continue
            const lfoIdx = lfoIdToIdx[link.lfoid]
            if (lfoIdx === undefined) continue
            const inputId = link.inputid || (link.input && link.input.id) || ''
            let fxIdx = -1
            let ctrlName = inputId
            const fxMatch = inputId.match(/^_(.+)-([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i)
            if (fxMatch) {
                ctrlName = fxMatch[1]
                const fxUuid = fxMatch[2]
                fxIdx = store.fxrack.findIndex(f => f.id === fxUuid)
                if (fxIdx === -1) continue
            } else if (ctrlName.startsWith('_')) {
                ctrlName = ctrlName.substring(1)
            }
            linkArr.push([lfoIdx, fxIdx, ctrlName])
        }

        // Markers: [x, y] (normalized 0-1 so they survive resize)
        const mkArr = store.markers.map(m => [
            Number((m.x / w).toFixed(4)),
            Number((m.y / h).toFixed(4))
        ])

        const tempoEl = qs('#tempo')

        return {
            gn: store.playergain ? store.playergain.value : '0.7',
            pt: store.pitchknob ? store.pitchknob.value : '0',
            vo: store.outvolume ? store.outvolume.value : '0.7',
            bp: tempoEl ? tempoEl.value : '120',
            tr: store.transpose,
            at: store.attack ? store.attack.value : '0.4',
            re: store.release ? store.release.value : '0.4',
            de: store.density ? store.density.value : '0.85',
            sp: store.spread ? store.spread.value : '0.2',
            pn: store.pan ? store.pan.value : '0.2',
            mk: mkArr,
            f: fxArr,
            l: lfoArr,
            k: linkArr,
            cv: (() => {
                const curves = {}
                const saveCurve = (el) => {
                    if (!el || !el.parentNode) return
                    const ps = el.parentNode.querySelector('.presetSelect')
                    if (ps && ps.value && ps.value !== 'linear') curves[el.id] = ps.value
                }
                saveCurve(store.pitchknob)
                saveCurve(store.playergain)
                saveCurve(store.outvolume)
                saveCurve(store.attack)
                saveCurve(store.release)
                saveCurve(store.density)
                saveCurve(store.spread)
                saveCurve(store.pan)
                store.fxrack.forEach(fx => {
                    const fxEl = qs(`.fxhold[data-id="${fx.id}"]`)
                    if (fxEl) fxEl.querySelectorAll('.ctrlinput').forEach(input => saveCurve(input))
                })
                return curves
            })()
        }
    };

    const compressState = (state) => {
        const json = JSON.stringify(state)
        const compressed = pako.deflateRaw(json)
        return urlSafeBase64Encode(compressed)
    };

    const decompressState = (encoded) => {
        const bytes = urlSafeBase64Decode(encoded)
        const json = new TextDecoder().decode(pako.inflateRaw(bytes))
        return JSON.parse(json)
    };

    const saveStateToURL = () => {
        const params = new URLSearchParams();
        const state = serializeState();
        params.set('s', compressState(state));
        const newURL = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState({}, '', newURL);
    };

    const loadStateFromURL = () => {
        const params = new URLSearchParams(window.location.search);
        let st = null;
        if (params.has('s')) {
            try { st = decompressState(params.get('s')); }
            catch (e) { console.error('[granulr] Failed to decompress state:', e); }
        }
        if (!st) return;

        const getVal = (shortKey) => st[shortKey] !== undefined ? st[shortKey] : undefined;

        // Sliders
        const applyInput = (el, val) => {
            if (val === undefined || !el) return
            el.value = val
            el.dispatchEvent(new Event('input'))
            el.dispatchEvent(new Event('change'))
        }
        applyInput(store.playergain, getVal('gn'))
        applyInput(store.pitchknob, getVal('pt'))
        applyInput(store.outvolume, getVal('vo'))
        applyInput(store.attack, getVal('at'))
        applyInput(store.release, getVal('re'))
        applyInput(store.density, getVal('de'))
        applyInput(store.spread, getVal('sp'))
        applyInput(store.pan, getVal('pn'))

        // BPM
        const bpVal = getVal('bp')
        if (bpVal !== undefined && tempoEl) {
            tempoEl.value = bpVal
            document.body.dispatchEvent(new CustomEvent('bpmchange', { detail: { bpm: Number(bpVal) } }))
        }

        // Transpose
        const trVal = getVal('tr')
        if (trVal !== undefined) store.transpose = Number(trVal)

        // FX
        if (st.f && st.f.length) {
            qsa('.fxhold').forEach(hold => hold.remove())
            store.fxrack.forEach(fx => { if (fx.destroy) fx.destroy(); })
            store.fxrack = []
            st.f.forEach((fxEntry) => {
                const [fxName, isOn, controls] = fxEntry
                if (!fxName) return
                const fxDef = fxs.fx.find(f => f.name === fxName)
                if (!fxDef) return
                if (arack.buildFX) arack.buildFX(fxDef)
                const newFx = store.fxrack[store.fxrack.length - 1]
                const fxEl = qs(`.fxhold[data-id="${newFx.id}"]`)
                if (fxEl) {
                    for (let ctrlName in controls) {
                        const input = fxEl.querySelector(`[data-label="${ctrlName}"]`)
                        if (input) {
                            input.value = controls[ctrlName]
                            input.dispatchEvent(new Event('input'))
                            input.dispatchEvent(new Event('change'))
                        }
                    }
                    if (!isOn) {
                        const onoff = fxEl.querySelector('.switch')
                        if (onoff) { onoff.checked = false; onoff.dispatchEvent(new Event('change')) }
                    }
                }
            })
        }

        // LFO
        if (st.l && st.l.length) {
            const bank = window.openedbank
            qsa('.lfo').forEach(el => el.remove())
            window.lfo[bank] = {}
            window.lfoz = window.lfo[bank]
            window.lfobtns[bank] = []
            window.lfolinks[bank] = {}
            window.lfoindex = 0

            const lfoIds = []
            st.l.forEach(lfoEntry => {
                const [name, wave, freq, amp, seed, syncToBPM, quantizeValue, phaseOffset] = lfoEntry
                lfo.createlfo({ name, wave, freq, amp, seed, syncToBPM: !!syncToBPM, quantizeValue, phaseOffset: parseFloat(phaseOffset) || 0 })
            })
            const lfoBankAfter = window.lfo[bank] || {}
            for (let id in lfoBankAfter) lfoIds.push(id)

            // Links
            if (st.k && st.k.length) {
                const linkBank = window.lfolinks[bank]
                st.k.forEach(linkEntry => {
                    const [lfoIdx, fxIdx, ctrlName] = linkEntry
                    const lfoid = lfoIds[lfoIdx]
                    if (!lfoid) return
                    const lfoInst = window.lfo[bank][lfoid]
                    if (!lfoInst) return
                    let rangeInput = null
                    let inputid = ''
                    if (fxIdx >= 0 && store.fxrack[fxIdx]) {
                        const fxId = store.fxrack[fxIdx].id
                        inputid = `_${ctrlName}-${fxId}`
                        rangeInput = document.getElementById(inputid)
                    } else {
                        inputid = ctrlName.startsWith('_') ? ctrlName : `_${ctrlName}`
                        rangeInput = document.getElementById(inputid)
                        if (!rangeInput) { inputid = ctrlName; rangeInput = document.getElementById(inputid) }
                    }
                    if (!rangeInput) return
                    const input = rangeInput.parentNode.querySelector(`.inpt_${CSS.escape(inputid)}`)
                    if (!input) return
                    const linkBtn = document.getElementById(`lfo_link_${lfoid}`)
                    const linkid = uuid()
                    linkBank[linkid] = {
                        lfoid: lfoid, lfo: lfoInst, inputid: inputid, input: input,
                        btnid: `lfo_link_${lfoid}`, linkid: linkid
                    }
                    if (linkBtn) linkBtn.innerHTML = ''
                })
                try { document.body.dispatchEvent(new CustomEvent('lfolinked')); } catch(e) {}
            }
        }

        // Markers — placed after buffer is loaded (deferred)
        if (st.mk && st.mk.length) {
            store._pendingMarkers = st.mk
        }

        // Curves
        if (st.cv) {
            const restoreCurve = (el, preset) => {
                if (!el || !el.parentNode) return
                const ps = el.parentNode.querySelector('.presetSelect')
                if (ps) { ps.value = preset; ps.dispatchEvent(new Event('change')) }
            }
            for (let inputId in st.cv) {
                const el = document.getElementById(inputId)
                if (el) restoreCurve(el, st.cv[inputId])
            }
        }
    };

    // Smart save timing
    debouncedSave = debounce(saveStateToURL, 2000);
    debouncedSaveStructural = debounce(saveStateToURL, 300);

    const flushSave = () => { if (typeof saveStateToURL === 'function') saveStateToURL(); };
    window.addEventListener('beforeunload', flushSave);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flushSave();
    });

    // Load state from URL on init
    _isLoading = true;
    loadStateFromURL();
    _isLoading = false;
    if (typeof debouncedSaveStructural && debouncedSaveStructural.cancel) debouncedSaveStructural.cancel();
    if (typeof debouncedSave && debouncedSave.cancel) debouncedSave.cancel();

    // Add event listeners for faders (value changes — slow debounce)
    [store.pitchknob, store.playergain, store.outvolume, store.attack, store.release, store.density, store.spread, store.pan].forEach(el => {
        if (!el) return
        el.addEventListener('input', debouncedSave)
        el.addEventListener('change', debouncedSave)
    })

    // FX value changes → structural save (fast)
    document.body.addEventListener('fxvaluechange', debouncedSave)
    document.body.addEventListener('fxstructuralchange', debouncedSaveStructural)

    // ---- Place pending markers once buffer loads ----
    // Markers from saved state are stored normalized (0-1) and placed after
    // the audio buffer loads and canvas is sized. We poll since loadBuffer
    // is async (bucket drop / file drop).
    let _pendingCheckDone = false
    const placePendingMarkers = () => {
        if (store._pendingMarkers && store._pendingMarkers.length) {
            setTimeout(() => {
                store._pendingMarkers.forEach(([nx, ny]) => {
                    createMarker(nx * w, ny * h)
                })
                store._pendingMarkers = null
            }, 100)
        }
    }
    const checkPending = () => {
        if (store.isloaded && store._pendingMarkers && !_pendingCheckDone) {
            _pendingCheckDone = true
            placePendingMarkers()
        }
    }

    // Start draw loop
    draw()
    // Poll for pending markers (from URL state) after buffer loads
    setInterval(checkPending, 500)

    // Resume audio context on first user interaction (browser autoplay policy)
    const resumeAudio = () => {
        if (store.audioContext.state === 'suspended') store.audioContext.resume()
    }
    document.addEventListener('click', resumeAudio, { once: true })
    document.addEventListener('keydown', resumeAudio, { once: true })

    // Keyboard shortcuts
    document.addEventListener('keydown', (event) => {
        if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') return
        if (event.code === 'KeyC') qs('#clearmarkers').click()
        if (event.code === 'KeyR') qs('#record').click()
        if (event.code === 'KeyT') qs('#taptempo').click()
    })
}

// Defer init to allow DOM + scripts to settle (matches kaeps pattern)
setTimeout(() => {
    init()
}, 200)
